import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { useLocale, useNotify, useTranslate } from 'react-admin';
import { PermissionButton } from 'src/routes/components';
import {
  stErrorMessage,
  REDPACKET_DISPLAY_RULES_KEY,
  StDisplayRules,
  emptyDisplayRules,
  stDeleteSystemConfig,
  stSystemConfig,
  stUpsertSystemConfig,
} from '../../../services/socialTradingService';

const CLAIM_THRESHOLD_CONFIG_KEY = 'redpacket.claim_threshold';
const DISPLAY_CURRENCY = 'USDC';

/**
 * The display rules live in the shared social-trading system configs, so both writes go through
 * POST / DELETE /social-trading/ledger/system-configs, which require `st-system-configs:update`.
 */
const PERMISSION_RESOURCE = 'st-system-configs';

type LocaleKey = 'cn' | 'en';

/** Map Admin UI locale (`cn` / `en` / others) → display-rules payload key. */
function displayRulesLang(uiLocale: string): LocaleKey {
  return uiLocale === 'cn' || uiLocale === 'zh' ? 'cn' : 'en';
}

function parseMinClaimAmount(value: unknown): string {
  if (!value || typeof value !== 'object') return '1';
  const raw = (value as { minClaimAmount?: unknown }).minClaimAmount;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return '1';
}

function normalizeRules(raw: unknown): StDisplayRules | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const obj = raw as Partial<StDisplayRules>;
  const cn = obj.cn;
  const en = obj.en;
  if (
    !cn ||
    !en ||
    typeof cn.title !== 'string' ||
    typeof en.title !== 'string' ||
    !Array.isArray(cn.rules) ||
    !Array.isArray(en.rules)
  ) {
    return null;
  }
  return {
    cn: { title: cn.title, rules: cn.rules.map(String) },
    en: { title: en.title, rules: en.rules.map(String) },
  };
}

function validateRules(rules: StDisplayRules): string | null {
  if (!rules.cn.title.trim() || !rules.en.title.trim()) {
    return 'title';
  }
  if (rules.cn.rules.length < 1 || rules.en.rules.length < 1) {
    return 'empty';
  }
  if (rules.cn.rules.length !== rules.en.rules.length) {
    return 'mismatch';
  }
  if (rules.cn.rules.some((r) => !r.trim()) || rules.en.rules.some((r) => !r.trim())) {
    return 'blank';
  }
  return null;
}

/** Prototype-aligned red packet display rules block for the ledger page. */
export default function DisplayRulesSection() {
  const t = useTranslate();
  const notify = useNotify();
  const uiLocale = useLocale();
  const viewLang = displayRulesLang(uiLocale);
  const [editOpen, setEditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLang, setPreviewLang] = useState<LocaleKey>(viewLang);
  const [minClaimAmount, setMinClaimAmount] = useState('1');
  const [hasConfig, setHasConfig] = useState(false);
  const [rules, setRules] = useState<StDisplayRules>(emptyDisplayRules());
  const [draft, setDraft] = useState<StDisplayRules>(emptyDisplayRules());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cfg, thresholdCfg] = await Promise.all([
        stSystemConfig(REDPACKET_DISPLAY_RULES_KEY),
        stSystemConfig(CLAIM_THRESHOLD_CONFIG_KEY),
      ]);
      setMinClaimAmount(parseMinClaimAmount(thresholdCfg?.value));
      const parsed = cfg?.value ? normalizeRules(cfg.value) : null;
      if (parsed) {
        setRules(parsed);
        setHasConfig(true);
      } else {
        setRules(emptyDisplayRules());
        setHasConfig(false);
      }
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const renderLocale = (source: StDisplayRules, lang: LocaleKey) => {
    const locale = source[lang];
    return {
      title: locale.title,
      rules: locale.rules.map((text) =>
        text.replaceAll('{minClaimAmount}', minClaimAmount).replaceAll('{currency}', DISPLAY_CURRENCY)
      ),
    };
  };

  const viewPreview = useMemo(
    () => renderLocale(rules, viewLang),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rules, minClaimAmount, viewLang]
  );
  const dialogPreview = useMemo(
    () => renderLocale(rules, previewLang),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewLang, rules, minClaimAmount]
  );

  const isEmpty = !hasConfig || (rules.cn.rules.length === 0 && !rules.cn.title.trim());

  const openEdit = () => {
    setDraft(structuredClone(hasConfig ? rules : emptyDisplayRules()));
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditOpen(false);
    setDraft(structuredClone(rules));
  };

  const updateLocale = (lang: LocaleKey, patch: Partial<{ title: string; rules: string[] }>) => {
    setDraft((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], ...patch },
    }));
  };

  const updateRule = (lang: LocaleKey, index: number, value: string) => {
    setDraft((prev) => {
      const next = [...prev[lang].rules];
      next[index] = value;
      return { ...prev, [lang]: { ...prev[lang], rules: next } };
    });
  };

  const addRule = () => {
    setDraft((prev) => ({
      cn: { ...prev.cn, rules: [...prev.cn.rules, ''] },
      en: { ...prev.en, rules: [...prev.en.rules, ''] },
    }));
  };

  const removeRule = (index: number) => {
    setDraft((prev) => ({
      cn: { ...prev.cn, rules: prev.cn.rules.filter((_, i) => i !== index) },
      en: { ...prev.en, rules: prev.en.rules.filter((_, i) => i !== index) },
    }));
  };

  const save = async () => {
    const err = validateRules(draft);
    if (err) {
      notify(t(`module.socialTrading.displayRulesValidation.${err}`), { type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await stUpsertSystemConfig({ key: REDPACKET_DISPLAY_RULES_KEY, value: draft });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setEditOpen(false);
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const clearConfig = async () => {
    setSaving(true);
    try {
      if (hasConfig) {
        await stDeleteSystemConfig(REDPACKET_DISPLAY_RULES_KEY);
      }
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setEditOpen(false);
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h6">{t('module.socialTrading.redpacketDisplayRules')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('module.socialTrading.redpacketDisplayRulesHint')}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={openEdit} sx={{ flexShrink: 0 }}>
          {t('module.socialTrading.editDisplayRules')}
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          {isEmpty ? (
            <Typography variant="body2" color="text.secondary">
              {t('module.socialTrading.displayRulesEmpty')}
            </Typography>
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">{viewPreview.title || '—'}</Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setPreviewLang(viewLang);
                    setPreviewOpen(true);
                  }}
                >
                  {t('module.socialTrading.userPreview')}
                </Button>
              </Stack>
              <Box component="ol" sx={{ pl: 3, m: 0 }}>
                {viewPreview.rules.map((rule, idx) => (
                  <Typography component="li" key={idx} variant="body1" sx={{ mb: 1.5, lineHeight: 1.7 }}>
                    {rule}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="md" scroll="paper">
        <DialogTitle>{t('module.socialTrading.redpacketDisplayRulesEdit')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                label={t('module.socialTrading.displayRulesTitleZh')}
                value={draft.cn.title}
                onChange={(e) => updateLocale('cn', { title: e.target.value })}
              />
              <TextField
                fullWidth
                size="small"
                label={t('module.socialTrading.displayRulesTitleEn')}
                value={draft.en.title}
                onChange={(e) => updateLocale('en', { title: e.target.value })}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                {t('module.socialTrading.displayRulesRefFields')}
              </Typography>
              <Chip size="small" label={`minClaimAmount = ${minClaimAmount}`} color="primary" variant="outlined" />
              <Chip size="small" label={`currency = ${DISPLAY_CURRENCY}`} variant="outlined" />
            </Stack>

            {draft.cn.rules.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('module.socialTrading.displayRulesEmptyEdit')}
              </Typography>
            )}

            {draft.cn.rules.map((_, idx) => (
              <Card key={`rule-${idx}`} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">
                      {t('module.socialTrading.displayRulesItem', { n: idx + 1 })}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="remove"
                      onClick={() => removeRule(idx)}
                      disabled={saving}
                    >
                      <FaTrash size={14} />
                    </IconButton>
                  </Stack>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('module.socialTrading.displayRulesCn')}
                    value={draft.cn.rules[idx]}
                    onChange={(e) => updateRule('cn', idx, e.target.value)}
                    multiline
                    minRows={2}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={t('module.socialTrading.displayRulesEn')}
                    value={draft.en.rules[idx]}
                    onChange={(e) => updateRule('en', idx, e.target.value)}
                    multiline
                    minRows={2}
                  />
                </CardContent>
              </Card>
            ))}

            <Stack direction="row" justifyContent="space-between">
              <Button startIcon={<FaPlus />} onClick={addRule} disabled={saving}>
                {t('module.socialTrading.displayRulesAdd')}
              </Button>
              <PermissionButton resource={PERMISSION_RESOURCE} action="update">
                <Button color="warning" disabled={saving || !hasConfig} onClick={clearConfig}>
                  {t('module.socialTrading.displayRulesClear')}
                </Button>
              </PermissionButton>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeEdit} disabled={saving}>
            {t('module.socialTrading.cancel')}
          </Button>
          <PermissionButton resource={PERMISSION_RESOURCE} action="update">
            <Button variant="contained" disabled={saving} onClick={save}>
              {t('module.socialTrading.saveAndApply')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('module.socialTrading.userPreview')}</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              size="small"
              variant={previewLang === 'cn' ? 'contained' : 'outlined'}
              onClick={() => setPreviewLang('cn')}
            >
              CN
            </Button>
            <Button
              size="small"
              variant={previewLang === 'en' ? 'contained' : 'outlined'}
              onClick={() => setPreviewLang('en')}
            >
              EN
            </Button>
          </Stack>
          {isEmpty ? (
            <Typography variant="body2" color="text.secondary">
              {t('module.socialTrading.displayRulesEmpty')}
            </Typography>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {dialogPreview.title || '—'}
              </Typography>
              <Box component="ol" sx={{ pl: 3, m: 0 }}>
                {dialogPreview.rules.map((rule, idx) => (
                  <Typography component="li" key={idx} variant="body1" sx={{ mb: 1.5 }}>
                    {rule}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>{t('module.socialTrading.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
